/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { getBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail-Server';
import { dataMap, fetcher } from '@/data/Content/_BreadcrumbTrail';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getContractIdParamFromContext, useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { DATA_KEY_BREADCRUMB } from '@/data/constants/dataKey';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { seoObfuscatorMiddleWare } from '@/data/utils/swr/seoObfuscatorMiddleWare';
import useSWR from 'swr';
import { Breadcrumb } from '@/data/types/Breadcrumb';
import { DYNAMIC_CONTENT_PARENT_PAGE_SEO_URL, PAGES_REQUIRE_BREADCRUMB_LABEL_MAPPING, STATIC_PAGENAME_TO_LABELS_MAPPING } from '@/data/constants/pageDataFromId';
import { MY_ACCOUNT_X_CUSTOM_FIELD_2 } from '@/data/constants/pageDataFromId';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { capitalize } from 'lodash';
import { useMemo } from 'react';
export { getBreadcrumbTrail };

const getTranslatedPageName = (pageName:string, x_customField2:string) => {
	if(x_customField2 === MY_ACCOUNT_X_CUSTOM_FIELD_2){
		return STATIC_PAGENAME_TO_LABELS_MAPPING[pageName]??pageName;
	}
	return pageName
}

const getDynamicContentPageBreadCrumb = (x_customField2:string, basePath:string, pageName:string) => {
	let breadcrumb:Breadcrumb[] = [
		{
			label: capitalize(x_customField2),
			href:`${basePath}/${DYNAMIC_CONTENT_PARENT_PAGE_SEO_URL[x_customField2?.toLocaleLowerCase()]}`
		},
		{
			label:getTranslatedPageName(pageName, x_customField2)
		}
	];
	return breadcrumb;
}

export const useBreadcrumbTrail = (categoryId?:string| undefined) => {
	const router = useNextRouter();
	const query = router.query;
	const { trail } = query;
	const { settings } = useSettings();
	const { user } = useUser();
	const { storeId, langId, defaultCatalogId: catalogId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();	
	const { data: pageData } = usePageDataFromId();
	let { tokenName = '', tokenValue = '', tokenExternalValue = '', x_customField2 = '' } = pageData ?? {};
	if(categoryId){
		tokenName = 'CategoryToken';
	 	tokenValue = categoryId;
	}
	const categoryCanonicalDefault= pageData?.tokenName?.includes("CategoryToken") 
									? '/'+pageData?.identifier 
									: null;

	const {
		pageName,
		displayPageName
	} = useMemo(()=>{
		const tempPageName = pageData?.page?.type?.includes("SearchPage") ? "SearchPage" : tokenExternalValue;
		const displayPageName = tempPageName?.includes('SearchPage') 
								? `Zoek: ${decodeURIComponent((query.searchTerm as string) || '')}`
								: PAGES_REQUIRE_BREADCRUMB_LABEL_MAPPING.includes(tempPageName)
								? STATIC_PAGENAME_TO_LABELS_MAPPING[tempPageName]
								: tempPageName;
		return {
			pageName : tempPageName,
			displayPageName
		}
	}, [pageData?.page?.type, tokenExternalValue, query?.searchTerm]);

	const { data, error } = useSWR(
		tokenName && storeId
			? [
					shrink({
						trail,
						tokenName,
						tokenValue,
						tokenExternalValue,
						storeId,
						catalogId,
						langId,
						...getContractIdParamFromContext(user?.context),
					}),
					DATA_KEY_BREADCRUMB,
			  ]
			: null,
		async ([props]) => dataMap(await fetcher(true)(expand(props), params), trail as string),
		{ use: [seoObfuscatorMiddleWare] }
	);
	let breadcrumb = data?.rc;
	if( x_customField2 && x_customField2!==EMPTY_STRING 
			&& ["MyAccountPagesToken", "StaticPagesToken"].includes(tokenName)){
		breadcrumb = getDynamicContentPageBreadCrumb(x_customField2, settings.userData.SiteLink_DomainName, pageName);
	}

	return { 
		breadcrumb: breadcrumb, 
		uniqueId: tokenValue, 
		categoryCanonicalPath:data?.categoryCanonicalPath??categoryCanonicalDefault,
		displayPageName,
		error
	};
};